import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { getClassById } from "../../market/warehouse-baserow/useAdminClasses";
import { useConfig } from "../../utensils/ConfigContext";
import { useUser } from "../../utensils/UserContext";

// Notificar Email.
import useMail from "../../market/warehouse-baserow/useMail";

// Helper para calcular minutos
const minutesDiff = (inicio, fin) => {
  if (!inicio || !fin) return 0;
  const [sh, sm] = String(inicio).split(":").map(Number);
  const [eh, em] = String(fin).split(":").map(Number);
  const diff = eh * 60 + em - (sh * 60 + sm);
  return diff > 0 ? diff : 0;
};

const SnackNewReservation = ({ show, onHide, classId, onSave }) => {
  const { apiToken } = useConfig();
  const { user } = useUser();

  const [clase, setClase] = useState(null);
  const [selectedHorario, setSelectedHorario] = useState("");
  const [saving, setSaving] = useState(false);
  const [reservasPorHorario, setReservasPorHorario] = useState({});
  const [diasReservados, setDiasReservados] = useState([]);

  // Instancia del hook para notificar via mail.
  const { sendReservationConfirmation } = useMail();

  // Cargar clase y reservas al abrir el modal
  useEffect(() => {
    const fetchClassAndReservations = async () => {
      if (classId && apiToken) {
        try {
          const claseData = await getClassById(classId, apiToken);
          setClase(claseData);

          const res = await fetch(
            `https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true&size=200`,
            {
              headers: {
                Authorization: `Token ${apiToken}`,
              },
            }
          );
          const data = await res.json();
          const agrupadas = {};
          const reservasUsuario = [];

          data.results.forEach((r) => {
            const clave = `${r.Clase}||${r.Reserva}`;
            if (!agrupadas[clave]) agrupadas[clave] = 0;
            agrupadas[clave]++;

            if (r.Email === user?.Email && r.Reserva) {
              reservasUsuario.push(r.Reserva.split(":")[0]); // guarda día (ej: "Lunes")
            }
          });

          setReservasPorHorario(agrupadas);
          setDiasReservados(reservasUsuario);
        } catch (error) {
          console.error("Error cargando datos:", error.message);
        }
      }
    };

    if (show) {
      setClase(null);
      setSelectedHorario("");
      setReservasPorHorario({});
      fetchClassAndReservations();
    }
  }, [show, classId, apiToken, user?.Email]);

  const checkAvailability = () => {
    const clave = `${classId}||${selectedHorario}`;
    const usados = reservasPorHorario[clave] || 0;
    return usados < (clase?.Cupo || 0);
  };

  const handleSubmit = async () => {
    if (!selectedHorario) {
      alert("Selecciona un horario");
      return;
    }

    setSaving(true);

    // Verificar disponibilidad de cupo
    const hayCupo = checkAvailability();
    if (!hayCupo) {
      alert("Este horario ya está lleno. Elige otro.");
      setSaving(false);
      return;
    }

    // Validar si ya tiene una reserva para el mismo día
    const diaSeleccionado = selectedHorario.split(":")[0];

    const resVerificacion = await fetch(
      `https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true&filters=%7B%22filter_type%22%3A%22AND%22%2C%22filters%22%3A%5B%7B%22type%22%3A%22date_is%22%2C%22field%22%3A%22Creado%20en%22%2C%22value%22%3A%22America%2FBogota%3F%3Fthis_week%22%7D%5D%2C%22groups%22%3A%5B%5D%7D`,
      {
        headers: {
          Authorization: `Token ${apiToken}`,
        },
      }
    );

    const data = await resVerificacion.json();

    const yaReservado = data.results.some(
      (r) =>
        r.Email === user?.Email &&
        typeof r.Reserva === "string" &&
        r.Reserva.startsWith(diaSeleccionado)
    );

    if (yaReservado) {
      alert(`Ya tienes una reserva activa para el día ${diaSeleccionado}.`);
      setSaving(false);
      return;
    }

    // Payload válido para guardar
    const payload = {
      Nombre: user?.Nombre || "",
      Email: user?.Email || "",
      Detalle: `Reserva de la clase ${clase?.Nombre}`,
      Estado: true,
      Clase: classId,
      Reserva: selectedHorario,
    };

    console.log("🟢 Payload enviado:", payload);

    try {
      const res = await fetch(
        "https://api.baserow.io/api/database/rows/table/569475/?user_field_names=true",
        {
          method: "POST",
          headers: {
            Authorization: `Token ${apiToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error al guardar: ${res.status} - ${text}`);
      }

      onSave();

      // Notificar via Mail.
      await sendReservationConfirmation(
        user?.Nombre || "",
        user?.Email || "",
        clase?.Nombre || "",
        selectedHorario
      );
    } catch (error) {
      alert("Error al guardar reserva");
      console.error("POST error:", error.message);
    } finally {
      setSaving(false);
    }
  };

  // Renderizar horarios con formato nuevo (días + título + franjas)
  const renderHorarios = () => {
    try {
      const programacion =
        typeof clase?.Programacion === "string"
          ? JSON.parse(clase.Programacion || "{}")
          : clase?.Programacion || {};

      const orderedDays = [
        "Lunes",
        "Martes",
        "Miércoles",
        "Jueves",
        "Viernes",
        "Sábado",
        "Domingo",
      ];

      return orderedDays.map((dia) => {
        const dayObj = programacion?.[dia] || { titulo: "", franjas: [] };
        const titulo = dayObj.titulo || "";
        const franjas = Array.isArray(dayObj.franjas) ? dayObj.franjas : [];

        return (
          <div key={`grp-${dia}`} className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="badge bg-light text-dark">{dia}</span>
              {titulo ? (
                <span className="badge bg-info text-dark">{titulo}</span>
              ) : null}
            </div>

            {franjas.length > 0 ? (
              franjas.map((f, idx) => {
                const mins = minutesDiff(f.inicio, f.fin);
                const label = `${dia}: ${f.inicio} - ${f.fin} (${mins} min)`;
                const clave = `${classId}||${label}`;
                const usados = reservasPorHorario[clave] || 0;
                const disponibles = Math.max((clase?.Cupo || 0) - usados, 0);
                const disabled = disponibles <= 0;

                return (
                  <Form.Check
                    key={`opt-${dia}-${idx}`}
                    type="radio"
                    name="horario"
                    id={`horario-${dia}-${idx}`}
                    label={
                      disabled
                        ? `${f.inicio} – ${f.fin} (${mins} min) — Sin cupos`
                        : `${f.inicio} – ${f.fin} (${mins} min) — ${disponibles} disponibles`
                    }
                    value={label}
                    checked={selectedHorario === label}
                    onChange={(e) => setSelectedHorario(e.target.value)}
                    className="mb-2"
                    disabled={disabled}
                  />
                );
              })
            ) : (
              <div className="text-muted ms-1">-</div>
            )}
          </div>
        );
      });
    } catch {
      return <p className="text-danger">No se puede leer la programación.</p>;
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {clase ? `Reservar clase: ${clase.Nombre}` : "Cargando clase..."}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {clase ? (
          <>
            <p>
              <strong>Instructor:</strong> {clase.Instructor}
            </p>
            <p>
              <strong>Detalle:</strong> {clase.Detalle}
            </p>
            <p>
              <strong>Cupo:</strong> {clase.Cupo}
            </p>
            <hr />
            <h6>Selecciona un horario</h6>
            <Form>{renderHorarios()}</Form>
          </>
        ) : (
          <p>Cargando información de la clase...</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={saving || !selectedHorario}
        >
          {saving ? "Guardando..." : "Confirmar reserva"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SnackNewReservation;
